import { Button, cn, DynamicMyoshLogo, ToggleButton, toast } from '@myosh/odin-components';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useGetGlobalPromptQuery, useUpdateGlobalPromptMutation } from '../api/enhanced/enhanced-bowtie-api';
import { useGetBowtieConfigurationQuery, useSetBowtieConfigurationMutation } from '../api/enhanced/enhanced-v4-api';
import LoadingPreview from '../components/common/loading-preview';
import { makeDataCy } from '../helpers/util';

type InputType = 'toggle' | 'textArea';

interface ConfigurationOptionType {
  label: string;
  name: string;
  value: boolean | string | undefined;
  type: InputType;
  placeholder?: string;
}

const configurationOptions: ConfigurationOptionType[] = [
  {
    label: 'AI Enabled',
    name: 'aiEnabled',
    value: false,
    type: 'toggle',
  },
  {
    label: 'Global Custom Instruction',
    name: 'globalCustomInstruction',
    value: '',
    type: 'textArea',
    placeholder: 'Provide extra detail about your situation to generate a more relevant response.',
  },
];

function Configuration() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: bowtieConfig,
    isFetching: isFetchingBowtieConfiguration,
    isError: isErrorBowtieConfiguration,
  } = useGetBowtieConfigurationQuery();
  const {
    data: globalPrompt,
    isFetching: isFetchingGlobalPrompt,
    isError: isErrorGlobalPrompt,
  } = useGetGlobalPromptQuery();
  const [setBowtieConfiguration] = useSetBowtieConfigurationMutation();
  const [setGlobalPrompt] = useUpdateGlobalPromptMutation();
  const [options, setOptions] = useState<ConfigurationOptionType[]>(configurationOptions);

  const handleClickCancel = () => {
    navigate(location.state?.from || '/', { replace: true });
  };

  const handleClickSave = async () => {
    try {
      const payload = {
        aiEnabled: !!options[0].value,
      };

      const [bowtieConfigurationRes, globalPromptRes] = await Promise.all([
        setBowtieConfiguration({ bowtieConfigDto: payload }).unwrap(),
        setGlobalPrompt({ globalPromptDto: { prompt: options[1].value as string } }).unwrap(),
      ]);

      if (!bowtieConfigurationRes.result || !globalPromptRes) {
        toast.error('Error saving configuration, please try again.');
      } else {
        toast.success('Configuration saved successfully');
        navigate(location.state?.from || '/', { replace: true });
      }
    } catch {
      toast.error('Error saving configuration, please try again.');
    }
  };
  const handleInputChange = (name: string, value: boolean | string | undefined) => {
    const updatedOptions = structuredClone(options).map((item) => {
      if (item.name === name) {
        return { ...item, value: value };
      }
      return item;
    });
    setOptions(updatedOptions);
  };

  const generateInputField = (option: ConfigurationOptionType, dataCyName: string) => {
    switch (option.type) {
      case 'toggle':
        return (
          <ToggleButton
            data-cy={`configuration-toggle-${dataCyName}`}
            onChange={(value) => handleInputChange(option.name, value)}
            checked={!!option.value}
          />
        );
      case 'textArea':
        return (
          <textarea
            data-cy={`configuration-textarea-${dataCyName}`}
            value={option.value as string}
            name={option.name}
            placeholder={option.placeholder}
            className="border-primary flex min-h-[500px] w-full resize-y appearance-none rounded-sm border px-2 py-1 text-sm focus:outline-hidden"
            onChange={(event) => handleInputChange(event.target.name, event.target.value)}
          />
        );
      default:
        break;
    }
  };

  useEffect(() => {
    if (!bowtieConfig || !globalPrompt) return;
    if (isErrorBowtieConfiguration || isErrorGlobalPrompt) {
      toast.error('Error fetching bowtie configuration, refresh the page to retry.');
      return;
    }
    const result: { [key: string]: boolean } = bowtieConfig || {};

    const updatedOptions = options.map((item) => {
      if (item.name === 'globalCustomInstruction') {
        return { ...item, value: globalPrompt?.prompt ?? item.value };
      }
      return { ...item, value: result[item.name] ?? item.value };
    });
    setOptions(updatedOptions);
  }, [bowtieConfig, globalPrompt, isErrorBowtieConfiguration, isErrorGlobalPrompt]);

  return (
    <div data-cy="configuration-page" className="flex h-full w-full flex-col items-center gap-8 px-16 py-2">
      <div className="flex h-12 w-full gap-8 align-bottom">
        <DynamicMyoshLogo />
        <p className="font-semibold text-4xl">Configuration</p>
      </div>
      <div className="flex h-[50%] w-[50%] flex-col gap-4 pb-8">
        {isFetchingBowtieConfiguration || isFetchingGlobalPrompt ? (
          <LoadingPreview message="" data-cy="configuration-loading-preview" />
        ) : (
          <>
            {options.map((item) => {
              const dataCyName = makeDataCy(item.name);
              const isTextArea = item.type === 'textArea';
              return (
                <div
                  key={item.name}
                  data-cy={`configuration-option-${dataCyName}`}
                  className={cn('flex w-full', {
                    'flex-col gap-2': isTextArea,
                    'flex-row justify-between': !isTextArea,
                  })}
                >
                  <p className="text-lg font-semibold">{item.label}</p>
                  {generateInputField(item, dataCyName)}
                </div>
              );
            })}
            <div className="mt-auto ml-auto flex flex-row gap-4">
              <Button variant="ghost" onClick={handleClickCancel} data-cy="configuration-button-cancel">
                Cancel
              </Button>
              <Button onClick={handleClickSave} data-cy="configuration-button-save">
                Save
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Configuration;
